import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { TOGGLE_EASY_MODE } from '@reduxLocal/actions/types';
import { store } from '@reduxLocal/persistState';
import type { DashboardNavigatorParamList } from '@navigation/types';

export type EasyModeRouteParams = NonNullable<DashboardNavigatorParamList['EasyMode']>;

type DispatchFn = (action: { type: string; payload?: unknown }) => void;

/**
 * Native/web registry so menu navigation can await save/leave before a hard reset.
 * When `navigateFn` is passed, a confirmed leave runs it instead of popping the Easy Mode route.
 */
type EasyModeCloseHandler = (navigateFn?: () => void) => Promise<boolean | undefined>;
let easyModeCloseHandler: EasyModeCloseHandler | null = null;

export function setEasyModeCloseHandler(handler: EasyModeCloseHandler | null) {
  easyModeCloseHandler = handler;
}

/** True while a confirmed leave is executing its navigation, so nested guards pass straight through. */
let leaveNavigationInProgress = false;

/**
 * Navigate first, then clear the flag in the same tick. Clearing it while Easy Mode is still the
 * focused route lets the screen's focus effect / DashboardStack sync re-enable it, which leaves the
 * destination rendered with Easy Mode shell padding and no footer.
 */
export function runEasyModeLeaveNavigation(
  navigateFn: () => void,
  dispatch: DispatchFn = store.dispatch as DispatchFn
) {
  setTimeout(() => {
    leaveNavigationInProgress = true;
    try {
      navigateFn();
    } catch (error) {
      console.warn('[EasyMode] leave navigation failed:', error);
    } finally {
      leaveNavigationInProgress = false;
      disableEasyModeFlag(dispatch);
    }
  }, 0);
}

/**
 * Single entry point for anything that navigates away from Easy Mode: runs the leave guard
 * (save/leave confirm), restores the footer by clearing the easy-mode flag, then navigates.
 * Resolves false when the user chose to stay.
 */
export async function leaveEasyModeAndNavigate(
  navigateFn: () => void,
  dispatch: DispatchFn = store.dispatch as DispatchFn
): Promise<boolean> {
  if (leaveNavigationInProgress || !store.getState().UI.easyMode) {
    navigateFn();
    return true;
  }

  if (easyModeCloseHandler) {
    const result = await easyModeCloseHandler(navigateFn);
    return result === true;
  }

  runEasyModeLeaveNavigation(navigateFn, dispatch);
  return true;
}

/** Whether a navigate(name, params) call targets the Easy Mode route itself (not an away navigation). */
export function isEasyModeNavigationTarget(name: unknown, params?: any): boolean {
  if (name === 'EasyMode') return true;
  if (name === 'Dashboard' && params?.screen === 'EasyMode') return true;
  if (name && typeof name === 'object') {
    const target = name as { name?: string; params?: any };
    return isEasyModeNavigationTarget(target.name, target.params);
  }
  return false;
}

const AWAY_ACTION_TYPES = new Set(['NAVIGATE', 'NAVIGATE_DEPRECATED', 'PUSH', 'REPLACE', 'RESET', 'JUMP_TO', 'POP_TO_TOP']);

/**
 * Navigation proxy for everything rendered inside Easy Mode (windows, cards, modals, render
 * components via `useNavigation`). Away navigations go through `leaveEasyModeAndNavigate`;
 * Easy Mode self-navigation, setParams, listeners, etc. delegate to the real object.
 * goBack/pop are left alone: they remove the route and hit the screen's beforeRemove guard.
 */
export function createEasyModeGuardedNavigation(navigation: any): any {
  if (!navigation) return navigation;

  const guard =
    (method: string, isAway: (...args: any[]) => boolean) =>
    (...args: any[]) => {
      if (!isAway(...args)) return navigation[method](...args);
      void leaveEasyModeAndNavigate(() => navigation[method](...args));
    };

  const proxy = Object.create(navigation);
  proxy.navigate = guard('navigate', (name, params) => !isEasyModeNavigationTarget(name, params));
  proxy.push = guard('push', (name, params) => !isEasyModeNavigationTarget(name, params));
  proxy.replace = guard('replace', (name, params) => !isEasyModeNavigationTarget(name, params));
  proxy.jumpTo = guard('jumpTo', (name, params) => !isEasyModeNavigationTarget(name, params));
  proxy.reset = guard('reset', () => true);
  proxy.popToTop = guard('popToTop', () => true);
  proxy.dispatch = guard('dispatch', (action) => {
    if (typeof action === 'function' || !action || !AWAY_ACTION_TYPES.has(action.type)) return false;
    return !isEasyModeNavigationTarget(action.payload?.name, action.payload?.params);
  });
  proxy.getParent = (...args: any[]) => createEasyModeGuardedNavigation(navigation.getParent?.(...args));
  return proxy;
}

/**
 * If Easy Mode is active, run the mounted leave guard (save prompt).
 * Returns false when the user cancels leave; true when navigation may proceed.
 */
export async function requestEasyModeLeave(): Promise<boolean> {
  if (!store.getState().UI.easyMode) return true;
  if (leaveNavigationInProgress) return true;

  if (easyModeCloseHandler) {
    const result = await easyModeCloseHandler();
    return result === true;
  }

  // Flag stuck on without a mounted screen — clear so UI does not stay locked.
  disableEasyModeFlag(store.dispatch as DispatchFn);
  return true;
}

export function enableEasyModeFlag(dispatch: DispatchFn) {
  if (!store.getState().UI.easyMode) {
    dispatch({ type: TOGGLE_EASY_MODE });
  }
}

export function disableEasyModeFlag(dispatch: DispatchFn) {
  if (store.getState().UI.easyMode) {
    dispatch({ type: TOGGLE_EASY_MODE });
  }
}

/** Navigate to the EasyMode dashboard route (unmounts the previous tab on web). */
export function navigateToEasyMode(
  navigation: NavigationProp<ParamListBase> | any,
  dispatch: DispatchFn,
  params?: EasyModeRouteParams
) {
  enableEasyModeFlag(dispatch);

  try {
    navigation.navigate('Dashboard', { screen: 'EasyMode', params });
    return;
  } catch {
    /* fall through */
  }

  try {
    navigation.navigate('EasyMode', params);
  } catch (error) {
    console.warn('[EasyMode] navigateToEasyMode failed:', error);
  }
}

/** Leave Easy Mode and return to the prior dashboard screen when possible. */
export function exitEasyModeRoute(navigation: NavigationProp<ParamListBase> | any, dispatch: DispatchFn) {
  disableEasyModeFlag(dispatch);

  if (typeof navigation.canGoBack === 'function' && navigation.canGoBack()) {
    navigation.goBack();
    return;
  }

  try {
    navigation.navigate('Dashboard', { screen: 'Welcome' });
    return;
  } catch {
    /* fall through */
  }

  try {
    navigation.navigate('Welcome');
  } catch (error) {
    console.warn('[EasyMode] exitEasyModeRoute fallback failed:', error);
  }
}

export function isOnEasyModeDashboardRoute(routeName: string | null | undefined): boolean {
  return routeName === 'EasyMode';
}
